import { Button, Space, Tooltip, message, Breadcrumb, Input, Avatar } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, HomeOutlined, SearchOutlined, BankOutlined, EyeOutlined } from '@ant-design/icons';
import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { schoolApi } from '../../../api/school.api';
import DataTable from '../../../components/common/DataTable';
import SchoolForm from '../components/SchoolForm';
import { showDeleteConfirm } from '../../../components/common/ConfirmModal';
import useDebounce from '../../../hooks/useDebounce';
import { resolveFileUrl } from '../../../utils/helpers';

const SchoolList = () => {
  const [formOpen,    setFormOpen]    = useState(false);
  const [editRecord,  setEditRecord]  = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [pagination,  setPagination]  = useState({ page: 1, limit: 10 });
  const [sort,        setSort]        = useState({ sortBy: 'createdAt', sortOrder: 'desc' });

  const queryClient = useQueryClient();
  const navigate    = useNavigate();

  const debouncedSearch = useDebounce(searchInput, 300);

  // ── Data ────────────────────────────────────────────────────────
  const { data, isLoading } = useQuery({
    queryKey: ['schools', debouncedSearch, sort, pagination],
    queryFn:  () => schoolApi.getAll({ search: debouncedSearch, ...sort, ...pagination }),
  });

  // ── Mutations ────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: schoolApi.create,
    onSuccess: () => { queryClient.invalidateQueries(['schools']); message.success('School created'); setFormOpen(false); },
    onError: (err) => message.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => schoolApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['schools']);
      message.success('School updated');
      setFormOpen(false);
      setEditRecord(null);
    },
    onError: (err) => message.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: schoolApi.delete,
    onSuccess: () => { queryClient.invalidateQueries(['schools']); message.success('School deleted'); },
    onError: (err) => message.error(err.message),
  });

  const handleFormSubmit = async (values) => {
    try {
      if (editRecord) { await updateMutation.mutateAsync({ id: editRecord.id, data: values }); }
      else            { await createMutation.mutateAsync(values); }
    } catch { /* error shown by onError */ }
  };

  // ── Search: real-time with 300ms debounce ────────────────────────
  const handleSearchChange = useCallback((e) => {
    setSearchInput(e.target.value);
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  // ── Sort handler ─────────────────────────────────────────────────
  const handleTableChange = useCallback((_pagination, _filters, sorter, extra) => {
    if (extra?.action !== 'sort') { return; }

    const nextSorter = Array.isArray(sorter) ? sorter[0] : sorter;

    setSort(nextSorter?.order
      ? {
          sortBy:    nextSorter.field || nextSorter.columnKey,
          sortOrder: nextSorter.order === 'ascend' ? 'asc' : 'desc',
        }
      : { sortBy: 'createdAt', sortOrder: 'desc' });
    setPagination((p) => ({ ...p, page: 1 }));
  }, []);

  const schools = data?.data || [];
  const meta    = data?.meta || {};

  const getSortOrder = (field) => (
    sort.sortBy === field ? (sort.sortOrder === 'asc' ? 'ascend' : 'descend') : null
  );

  // ── Columns ──────────────────────────────────────────────────────
  const columns = [
    {
      title: 'Logo', dataIndex: 'logo', key: 'logo', width: 64,
      render: (v, record) => (
        <Avatar
          src={v ? resolveFileUrl(v) : null}
          icon={!v && <BankOutlined />}
          size={36}
          shape="square"
          alt={record.name}
        />
      ),
    },
    {
      title: 'School Name', dataIndex: 'name', key: 'name',
      ellipsis: { showTitle: false }, sorter: true, sortOrder: getSortOrder('name'),
      render: (v) => <Tooltip title={v}>{v}</Tooltip>,
    },
    {
      title: 'Contact', key: 'contact',
      width: 140,
      render: (_, record) => {
        const contact = record.mobile || record.landline || '—';
        return contact;
      },
    },
    {
      title: 'Email', dataIndex: 'email', key: 'email',
      width: 220, ellipsis: { showTitle: false },
      sorter: true, sortOrder: getSortOrder('email'),
      render: (v) => v ? <Tooltip title={v}>{v}</Tooltip> : '—',
    },
    {
      title: 'Actions', key: 'actions', fixed: 'right', width: 120,
      render: (_, record) => (
        <Space size={6}>
          <Tooltip title="View Children">
            <Button
              shape="circle"
              size="small"
              icon={<EyeOutlined />}
              style={{ color: 'var(--wl-primary)', borderColor: 'var(--wl-primary)', background: '#ffffff' }}
              onClick={() => navigate(`/children?school=${record.id}`)}
            />
          </Tooltip>
          <Tooltip title="Edit">
            <Button
              shape="circle"
              size="small"
              icon={<EditOutlined />}
              style={{ color: 'var(--wl-primary)', borderColor: 'var(--wl-primary)', background: '#ffffff' }}
              onClick={() => { setEditRecord(record); setFormOpen(true); }}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              shape="circle"
              size="small"
              icon={<DeleteOutlined />}
              danger
              style={{ color: '#ff4d4f', borderColor: '#ff4d4f', background: '#fff2f0' }}
              onClick={() => showDeleteConfirm({
                content: 'Are You Sure You Want To Delete this school?',
                onOk: () => deleteMutation.mutate(record.id),
              })}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div>
      {/* Breadcrumb */}
      <Breadcrumb
        style={{ marginBottom: 14 }}
        items={[
          { title: <HomeOutlined onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }} /> },
          { title: 'Schools' },
        ]}
      />

      {/* Toolbar */}
      <div style={{
        display:      'flex',
        alignItems:   'center',
        gap:          10,
        marginBottom: 16,
        background:   '#fff',
        borderRadius: 14,
        padding:      '10px 16px',
        boxShadow:    '0 2px 8px rgba(var(--wl-primary-rgb),0.08)',
        border:       '1px solid var(--wl-primary-pale)',
      }}>
        <Input
          prefix={<SearchOutlined style={{ color: 'var(--wl-text-muted)', fontSize: 14 }} />}
          placeholder="Search by name, contact, email…"
          value={searchInput}
          onChange={handleSearchChange}
          allowClear
          style={{ flex: 1, maxWidth: 340, borderRadius: 9999, borderColor: 'var(--wl-primary-pale)' }}
        />

        <Button
          type="primary"
          icon={<PlusOutlined />}
          style={{ marginLeft: 'auto' }}
          onClick={() => { setEditRecord(null); setFormOpen(true); }}
        >
          Add School
        </Button>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        dataSource={schools}
        loading={isLoading}
        rowKey="id"
        tableLayout="fixed"
        scroll={{ x: 800 }}
        pagination={{
          current:  pagination.page,
          pageSize: pagination.limit,
          total:    meta.total,
          onChange: (page, limit) => setPagination({ page, limit }),
        }}
        onChange={handleTableChange}
      />

      {/* Add / Edit form */}
      <SchoolForm
        open={formOpen}
        onClose={() => { setFormOpen(false); setEditRecord(null); }}
        onSubmit={handleFormSubmit}
        initialValues={editRecord}
      />
    </div>
  );
};

export default SchoolList;
